import type { ClubPhotoDto } from '@customTypes/types';
import { ClubPhotoTag } from '@customTypes/types';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useClubProfilePhotoListQuery,
  useDeletePhotoItemMutation,
  useUpdatePhotoItemMutation,
  useUploadPhotoMutation
} from '@/shared/api/requests';
import { APP_CONST } from '@/shared/constants';

type ClubPhotoForm = {
  HallNoGuests: ClubPhotoDto & {
    photoId: string | null;
  };
  HallWithGuests: ClubPhotoDto & {
    photoId: string | null;
  };
  PrivateRooms: ClubPhotoDto & {
    photoId: string | null;
  };
  Scene: ClubPhotoDto & {
    photoId: string | null;
  };
};

export type ClubPhotoFormKey = keyof ClubPhotoForm;

export const usePhotoPage = () => {
  const { formatMessage } = useIntl();
  const { id: clubId } = useParams({ from: '/clubOwner/$id' });
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: '/clubOwner/$id/onboard/$step' });

  const uploadMutation = useUploadPhotoMutation();

  const updatePhotoMutation = useUpdatePhotoItemMutation();
  const deletePhotoMutation = useDeletePhotoItemMutation();

  const profilePhotosList = useClubProfilePhotoListQuery({ profileId: clubId });

  const queries = [profilePhotosList];
  const isLoading = queries.some((q) => q.isLoading);

  const [isProfile, setIsProfile] = useState('');
  const form = useForm<ClubPhotoForm>({
    initialValues: {
      HallNoGuests: {
        file: null,
        title: formatMessage({
          id: 'pages.onboarding.club.photo.options.hallNoGuests'
        }),
        description: '',
        tag: ClubPhotoTag.HallNoGuests,
        ClubProfileId: '',
        IsProfile: false,
        photoId: ''
      },
      HallWithGuests: {
        file: null,
        title: formatMessage({
          id: 'pages.onboarding.club.photo.options.hallWithGuests'
        }),
        description: '',
        tag: ClubPhotoTag.HallWithGuests,
        ClubProfileId: '',
        IsProfile: false,
        photoId: ''
      },
      PrivateRooms: {
        file: null,
        title: formatMessage({
          id: 'pages.onboarding.club.photo.options.privateRooms'
        }),
        description: '',
        tag: ClubPhotoTag.PrivateRooms,
        ClubProfileId: '',
        IsProfile: false,
        photoId: ''
      },
      Scene: {
        file: null,
        title: formatMessage({
          id: 'pages.onboarding.club.photo.options.scene'
        }),
        description: '',
        tag: ClubPhotoTag.Scene,
        ClubProfileId: '',
        IsProfile: false,
        photoId: ''
      }
    }
  });

  const next = () => {
    navigate({
      to: '/clubOwner/$id/onboard/$step',
      params: {
        step: 'video'
      }
    });
  };

  useEffect(() => {
    if (profilePhotosList.isSuccess && profilePhotosList.data?.data.length) {
      Object.entries(form.getValues()).forEach(([key, value]) => {
        const currentItem = profilePhotosList.data.data.filter(
          (item) => item.tag === value.tag
        );
        if (currentItem.length) {
          form.setFieldValue(`${key}.file`, currentItem[0].filePath);
          form.setFieldValue(`${key}.IsProfile`, currentItem[0].isProfile);
          form.setFieldValue(`${key}.photoId`, currentItem[0].id);

          if (currentItem[0].isProfile) setIsProfile(currentItem[0].tag!);
        }
      });
    }
  }, [profilePhotosList.data?.data]);

  const onSelectProfilePhoto = (value: string) => {
    setIsProfile(value);
    Object.entries(form.getValues()).forEach(([key]) => {
      const currentField = form.getValues()[key as ClubPhotoFormKey];
      if ((value as ClubPhotoTag) === currentField.tag) {
        return form.setFieldValue(`${key}.IsProfile`, true);
      }
      form.setFieldValue(`${key}.IsProfile`, false);
    });
  };

  const isValidForm =
    form.getValues().HallNoGuests.file &&
    form.getValues().HallWithGuests.file &&
    form.getValues().PrivateRooms.file &&
    form.getValues().Scene.file;

  const isSelectedProfilePhoto =
    form.getValues().HallNoGuests.IsProfile ||
    form.getValues().HallWithGuests.IsProfile ||
    form.getValues().Scene.IsProfile ||
    form.getValues().PrivateRooms.IsProfile;

  const onDeletePhoto = async (photoId: string | null, tag: ClubPhotoTag) => {
    if (photoId) {
      await deletePhotoMutation.mutateAsync(
        {
          params: {
            photoId
          }
        },
        {
          onSuccess: () => {
            form.setFieldValue(`${tag}.file`, null);
            form.setFieldValue(`${tag}.photoId`, null);
            queryClient.invalidateQueries({
              queryKey: ['ClubProfilePhotoList', clubId]
            });
          }
        }
      );
    }
  };

  const onSubmit = form.onSubmit(async (values) => {
    form.setSubmitting(true);

    localStorage.setItem(
      APP_CONST.LOCAL_STORAGE.IS_FIRST_LOGIN,
      JSON.stringify(false)
    );
    try {
      const promises = Object.entries(values).map(async ([, value]) => {
        const currentImage = profilePhotosList.data?.data.filter(
          (item) => item.tag === value.tag
        );

        if (
          typeof value.file === 'string' &&
          value.IsProfile &&
          currentImage?.length
        ) {
          await updatePhotoMutation.mutateAsync({
            params: {
              photoId: currentImage[0].id.toString(),
              data: { isProfile: true }
            }
          });
        }

        if (
          typeof value.file === 'string' &&
          !value.IsProfile &&
          currentImage?.length &&
          isProfile !== ''
        ) {
          await updatePhotoMutation.mutateAsync({
            params: {
              photoId: currentImage[0].id.toString(),
              data: { isProfile: false }
            }
          });
        }

        if (typeof value.file === 'string') {
          return;
        }

        const formData = new FormData();
        formData.append(`File`, value.file!);
        formData.append(`Title`, value.title);
        formData.append(`Description`, value.description);
        formData.append(`Tag`, value.tag);
        formData.append(`ClubProfileId`, clubId);
        formData.append(`IsProfile`, JSON.stringify(value.IsProfile || false));

        await uploadMutation.mutateAsync(
          {
            params: {
              formData
            }
          },
          {
            onSuccess: async () => {
              notifications.show({
                title: formatMessage({ id: 'notifications.successDataUpdate' }),
                ...notificationStyleConfig
              });
              if (currentImage?.length) {
                await deletePhotoMutation.mutateAsync({
                  params: {
                    photoId: currentImage[0].id.toString()
                  }
                });
              }
            },
            onError: () => {
              notifications.show({
                title: formatMessage({ id: 'notifications.errorOnDataUpdate' }),
                ...notificationStyleConfig
              });
            }
          }
        );
      });

      await Promise.all(promises);
      await queryClient.invalidateQueries({
        queryKey: ['ClubProfilePhotoList', clubId]
      });
      next();
    } finally {
      form.setSubmitting(false);
    }
  });

  return {
    state: {
      isLoading,
      form,
      isProfile,
      isSelectedProfilePhoto,
      isValidForm
    },
    functions: { onSubmit, onSelectProfilePhoto, onDeletePhoto }
  };
};
